/**
 * Execução do checklist — desenhada para o celular, no pátio.
 *
 * As decisões de UX aqui não são estéticas:
 *
 * - **Uma seção por vez.** 44 itens numa rolagem só é como se perde o lugar com
 *   luva na mão. A barra de progresso diz quanto falta no checklist inteiro.
 * - **Resposta em botão grande**, não em `<select>`: um toque, alvo largo.
 * - **Salva sozinho, item a item.** Cada resposta vai para a API assim que é
 *   dada. Não existe "botão salvar" que o operador esqueça — e como o
 *   salvamento é upsert por item, reenviar não duplica nada.
 * - **A foto entra pela câmera** (`capture="environment"`), sem passar por
 *   galeria.
 *
 * Concluída, a tela vira leitura: a execução preserva a versão do formulário e
 * as respostas não mudam mais.
 *
 * A mesma tela serve às duas experiências (docs/manutencao/02): dentro do SIAG
 * web (`variant="web"`) e no Portal Operacional (`variant="portal"`). Muda só a
 * moldura — de onde se volta e onde fica a barra fixa. Toda resposta passa pela
 * fila local (`answer-outbox`): rede caiu, nada se perde.
 */
import { useRunTireOptions, type RunTireOption } from "../features/tires/useRunTireOptions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceAnswerDto,
  MaintenanceAttachmentDto,
  MaintenanceDamageMapDto,
  MaintenanceItemDto,
  MaintenancePreviousCountDto,
  MaintenanceRunDetailDto,
  MaintenanceVehicleContextDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { IconAlertTriangle, IconCamera, IconCheck, IconChevronRight, IconTruck, IconUser, IconWrench } from "../components/icons";
import { ItemIcon } from "../features/maintenance/ItemIcon";
import { AnswerOutbox, toApiPayload, type PendingAnswer } from "../features/maintenance/answer-outbox";
import { DamageMap } from "../features/maintenance/DamageMap";
import { EvidenceThumb } from "../features/maintenance/EvidenceThumbs";
import { PhotoOutbox, type PendingPhoto, type UploadOutcome } from "../features/maintenance/photo-outbox";
import { PhotoReview } from "../features/maintenance/PhotoReview";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { avaliarItem, estadoDoItem, resumirExecucao, type EstadoItem, type EstadoSecao } from "../features/maintenance/run-summary";
import { portalPaths } from "../features/portal/portal-nav";
import { useMobile } from "../features/maintenance-mobile/mobile-ui";
import {
  CONFORMITY_CHOICES, CRITICALITY, CriticalityBadge, KIND_LABEL, MOVEMENT_LABEL, RUN_STATUS, YES_NO_CHOICES,
} from "../features/maintenance/labels";

const ORDEM_CRITICIDADE = ["low", "medium", "high"] as const;

export function MaintenanceRunPage({ variant = "web" }: { variant?: "web" | "portal" }) {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const portal = variant === "portal";
  const { can } = useCapabilities();
  const navigate = useNavigate();
  const [secaoAtual, setSecaoAtual] = useState(0);
  // "executar" → "revisar" → (confirmação) → concluída, que abre em leitura
  const [etapa, setEtapa] = useState<"executar" | "revisar">("executar");
  const [idxMobile, setIdxMobile] = useState(0);
  // Desktop: item cujo "Registro de avaria" está aberto no painel lateral
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);
  // O portal é o app de pátio: sempre o fluxo de celular, em qualquer largura.
  // A casca mobile do perfil Manutenção também (celular e tablet).
  const casca = useMobile();
  const ehMobile = useEhMobile(portal || casca);
  const [erro, setErro] = useState<string | null>(null);
  const [km, setKm] = useState("");
  const [motivoKm, setMotivoKm] = useState("");
  const [cancelando, setCancelando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["maintenance", "run", id],
    queryFn: () => api.get<MaintenanceRunDetailDto>(`/maintenance/runs/${id}`),
  });

  // Itens da seção "pneus" podem apontar QUAL pneu (mapa do cavalo e da carreta).
  const temSecaoPneus = !!data?.version.sections.some((s) => s.code === "pneus");
  const opcoesPneu = useRunTireOptions([data?.run.vehiclePlate, data?.run.trailerPlate], temSecaoPneus);

  // ── Autosave com fila local ────────────────────────────────────────────
  const outbox = useMemo(() => new AnswerOutbox(id), [id]);
  const [pendentes, setPendentes] = useState<PendingAnswer[]>(() => outbox.list());
  const [enviando, setEnviando] = useState(false);
  const [semRede, setSemRede] = useState(false);
  const emVoo = useRef(false);

  const enviarPendentes = useCallback(async () => {
    if (emVoo.current) return;
    const lote = outbox.list();
    if (lote.length === 0) return;
    emVoo.current = true;
    setEnviando(true);
    try {
      await api.put(`/maintenance/runs/${id}/answers`, { answers: lote.map(toApiPayload) });
      outbox.acknowledge(lote);
      setSemRede(false);
      setErro(null);
      await qc.invalidateQueries({ queryKey: ["maintenance", "run", id] });
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) {
        // Recusa da regra (checklist concluído, valor inválido): reenviar não resolve.
        outbox.acknowledge(lote);
        setErro(e.message);
      } else {
        setSemRede(true);
      }
    } finally {
      emVoo.current = false;
      setEnviando(false);
      setPendentes(outbox.list());
    }
  }, [id, outbox, qc]);

  const responder = useCallback((itemId: string, valores: Omit<PendingAnswer, "itemId" | "queuedAt">) => {
    setErro(null);
    outbox.enqueue({ itemId, ...valores });
    setPendentes(outbox.list());
    void enviarPendentes();
  }, [outbox, enviarPendentes]);

  // Retomada: o que ficou na fila (app fechado, rede caída) sai ao abrir, ao voltar
  // a conexão e a cada 15 s enquanto houver pendência.
  useEffect(() => {
    void enviarPendentes();
    const online = () => void enviarPendentes();
    window.addEventListener("online", online);
    return () => window.removeEventListener("online", online);
  }, [enviarPendentes]);
  useEffect(() => {
    if (pendentes.length === 0) return;
    const t = window.setInterval(() => void enviarPendentes(), 15_000);
    return () => window.clearInterval(t);
  }, [pendentes.length, enviarPendentes]);

  // ── Fotos: conferir → fila local → envio ──────────────────────────────────
  const fotos = useMemo(() => new PhotoOutbox(id), [id]);
  const [fotosPendentes, setFotosPendentes] = useState<PendingPhoto[]>([]);
  const [enviandoFotos, setEnviandoFotos] = useState(false);
  const [revisando, setRevisando] = useState<{ itemId: string; label: string; file: Blob } | null>(null);
  const fotosEmVoo = useRef(false);
  // Id da resposta NO SERVIDOR por item — a foto só sobe quando ele existe.
  const respostaNoServidor = useRef(new Map<string, string>());
  useEffect(() => {
    respostaNoServidor.current = new Map((data?.run.answers ?? []).map((a) => [a.itemId, a.id]));
  }, [data?.run.answers]);

  const recarregarFotos = useCallback(async () => setFotosPendentes(await fotos.list()), [fotos]);

  const enviarFotos = useCallback(async () => {
    if (fotosEmVoo.current) return;
    fotosEmVoo.current = true;
    setEnviandoFotos(true);
    try {
      const r = await fotos.flush(
        (itemId) => respostaNoServidor.current.get(itemId),
        async (p, answerId): Promise<UploadOutcome> => {
          try {
            await api.uploadWithFields("/maintenance/attachments", p.blob, { scope: "answer", answerId }, p.fileName);
            return { ok: true };
          } catch (e) {
            const regra = e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429;
            return { ok: false, retry: !regra, message: e instanceof ApiError ? e.message : "sem conexão" };
          }
        },
      );
      if (r.rejected.length) setErro(`Foto recusada: ${r.rejected.join("; ")}`);
      if (r.sent > 0) await qc.invalidateQueries({ queryKey: ["maintenance", "run", id] });
    } finally {
      fotosEmVoo.current = false;
      setEnviandoFotos(false);
      await recarregarFotos();
    }
  }, [fotos, id, qc, recarregarFotos]);

  // Retomada: ao abrir, ao voltar a rede, quando a resposta do item chega ao
  // servidor (a foto que esperava pode subir) e a cada 15 s enquanto houver fila.
  useEffect(() => {
    void recarregarFotos().then(() => enviarFotos());
    const online = () => void enviarFotos();
    window.addEventListener("online", online);
    return () => window.removeEventListener("online", online);
  }, [recarregarFotos, enviarFotos]);
  useEffect(() => {
    if (fotosPendentes.length) void enviarFotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.run.answers]);
  useEffect(() => {
    if (fotosPendentes.length === 0) return;
    const t = window.setInterval(() => void enviarFotos(), 15_000);
    return () => window.clearInterval(t);
  }, [fotosPendentes.length, enviarFotos]);

  const usarFoto = async (itemId: string, blob: Blob) => {
    setRevisando(null);
    setErro(null);
    // Item de foto sem resposta: a foto É a resposta — cria a linha para ela pendurar.
    if (!respostas.get(itemId)) responder(itemId, { textValue: null });
    await fotos.enqueue(itemId, blob, `foto-${Date.now()}.jpg`);
    await recarregarFotos();
    void enviarFotos();
  };

  const removerFoto = async (anexoId: string) => {
    try {
      await api.del(`/maintenance/attachments/${anexoId}`);
      await qc.invalidateQueries({ queryKey: ["maintenance", "run", id] });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível remover a foto.");
    }
  };

  const concluir = useMutation({
    mutationFn: () =>
      api.post(`/maintenance/runs/${id}/complete`, {
        odometerKm: Number(km),
        odometerOverrideReason: motivoKm || null,
      }),
    onSuccess: () => {
      setErro(null);
      setConfirmando(false);
      setEtapa("executar");
      window.scrollTo({ top: 0 });
      qc.invalidateQueries({ queryKey: ["maintenance"] });
    },
    onError: (e) => { setConfirmando(false); setErro(e instanceof ApiError ? e.message : "Não foi possível concluir."); },
  });

  // Checklist aberto por engano (placa errada, tipo errado) não pode ficar
  // "em andamento" para sempre. A API já recusa cancelar o que foi concluído.
  const cancelar = useMutation({
    mutationFn: () =>
      api.post(`/maintenance/runs/${id}/cancel`, { reason: motivoCancelamento.trim() || null }),
    onSuccess: () => {
      setErro(null);
      setCancelando(false);
      qc.invalidateQueries({ queryKey: ["maintenance"] });
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível cancelar."),
  });

  const run = data?.run;
  const version = data?.version;
  const contagensAnteriores = data?.previousCounts ?? {};
  const somenteLeitura = run?.status === "completed" || run?.status === "cancelled" || !can("checklist.execute");

  // Pendente local por cima do servidor: o que o usuário marcou não "volta atrás"
  // enquanto a resposta viaja.
  const respostas = useMemo(
    () => outbox.overlay(run?.answers ?? []) as Map<string, MaintenanceAnswerDto>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run?.answers, pendentes, outbox],
  );

  const anexosPorResposta = useMemo(() => {
    const m = new Map<string, MaintenanceAttachmentDto[]>();
    for (const at of run?.attachments ?? []) {
      if (!at.answerId) continue;
      m.set(at.answerId, [...(m.get(at.answerId) ?? []), at]);
    }
    return m;
  }, [run?.attachments]);

  const todosItens = useMemo(() => (version?.sections ?? []).flatMap((s) => s.items), [version]);
  const fotosDoItem = (itemId: string) => {
    const r = respostas.get(itemId);
    const enviadas = r?.id ? anexosPorResposta.get(r.id)?.length ?? 0 : 0;
    return enviadas + fotosPendentes.filter((p) => p.itemId === itemId).length;
  };
  const resumo = version ? resumirExecucao(version, respostas, contagensAnteriores, fotosDoItem) : null;
  // O que a conclusão vai gerar — mostrado ANTES, na revisão e na confirmação.
  const viramOcorrencia = todosItens.filter((i) => avaliarItem(i, respostas.get(i.id), contagensAnteriores[i.code]).naoConforme);
  const semProblema = viramOcorrencia.filter((i) => {
    const opcoes = i.defectOptions ?? [];
    if (!opcoes.length) return false;
    const r = respostas.get(i.id);
    const escolhido = opcoes.find((o) => o.value === r?.defectCode);
    return !escolhido || (escolhido.requiresObservation && !r?.observation);
  });
  const semFotoObrigatoria = viramOcorrencia.filter((i) => {
    const r = respostas.get(i.id);
    const elevada = r?.criticality === "high" && i.criticality !== "high";
    return (i.requiresPhotoOnNonConformity || elevada) && fotosDoItem(i.id) === 0;
  });

  useEffect(() => {
    if (run?.odometerKm != null) setKm(String(run.odometerKm));
  }, [run?.odometerKm]);

  const contexto = useQuery({
    queryKey: ["maintenance", "vehicle-context", run?.vehiclePlate],
    queryFn: () => api.get<MaintenanceVehicleContextDto>(`/maintenance/vehicle-context?plate=${run!.vehiclePlate}`),
    enabled: !!run?.vehiclePlate,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (isLoading || !run || !version || !resumo) {
    return <div className="p-8 text-sm text-gray-500">Carregando checklist…</div>;
  }

  const voltar = portal ? portalPaths.checklists : "/frota/manutencao/checklists";
  const ncsLink = portal ? `${portalPaths.occurrences}?plate=${run.vehiclePlate}` : `/frota/manutencao/nao-conformidades?plate=${run.vehiclePlate}`;
  const concluida = run.status === "completed";
  const fluxo = somenteLeitura ? "leitura" : etapa;

  // Celular: um item por vez, na ordem do formulário. A seção acompanha o item.
  const plano = version.sections.flatMap((s, si) => s.items.map((it) => ({ si, it })));
  const posMobile = Math.min(idxMobile, Math.max(plano.length - 1, 0));
  const atualMobile = plano[posMobile];
  const secaoIdx = ehMobile && fluxo === "executar" ? (atualMobile?.si ?? 0) : secaoAtual;
  const secao = version.sections[secaoIdx];

  const irParaSecao = (i: number) => {
    setSecaoAtual(i);
    const primeiro = plano.findIndex((p) => p.si === i);
    if (primeiro >= 0) setIdxMobile(primeiro);
    setEtapa("executar");
    window.scrollTo({ top: 0 });
  };
  const irParaItem = (itemId: string, si: number) => {
    setSecaoAtual(si);
    const pos = plano.findIndex((p) => p.it.id === itemId);
    if (pos >= 0) setIdxMobile(pos);
    setEtapa("executar");
    window.scrollTo({ top: 0 });
  };
  const salvarERetornar = async () => {
    await enviarPendentes();
    await enviarFotos();
    navigate(voltar);
  };

  const todosObrigatoriosOk = resumo.obrigatoriosPendentes.length === 0;
  const podeFinalizar = km !== "" && todosObrigatoriosOk && pendentes.length === 0 && fotosPendentes.length === 0 && !concluir.isPending;
  const ultimoDeTodos = ehMobile ? posMobile >= plano.length - 1 : secaoAtual >= version.sections.length - 1;
  const corProgresso = resumo.naoConformes > 0 ? "bg-danger-600" : resumo.atencao > 0 ? "bg-warning-500" : "bg-tm-500";

  const propsDoItem = (item: MaintenanceItemDto, s: typeof secao): ItemProps => ({
    item,
    resposta: respostas.get(item.id),
    anterior: contagensAnteriores[item.code],
    anexos: respostas.get(item.id) ? (anexosPorResposta.get(respostas.get(item.id)!.id) ?? []) : [],
    somenteLeitura,
    salvando: enviando,
    estado: estadoDoItem(item, respostas.get(item.id), contagensAnteriores[item.code], fotosDoItem(item.id)),
    onResponder: (valores) => responder(item.id, valores),
    fotosNaFila: fotosPendentes.filter((p) => p.itemId === item.id),
    onAnexo: (file) => setRevisando({ itemId: item.id, label: item.label, file }),
    onDescartarFila: async (fotoId) => { await fotos.discard(fotoId); await recarregarFotos(); },
    onRemoverAnexo: removerFoto,
    pneus: s?.code === "pneus" && opcoesPneu.length > 0 ? opcoesPneu : undefined,
  });
  const renderItem = (item: MaintenanceItemDto, s: typeof secao, celular = false) => (
    <ItemLinha key={item.id} {...propsDoItem(item, s)} celular={celular} />
  );

  // Desktop: conformidade e sim/não viram linha de tabela; o resto (quantidade,
  // mapa de avarias, texto, foto) precisa de espaço e segue como cartão.
  const cabeNaTabela = (i: MaintenanceItemDto) => i.answerType === "conformity" || i.answerType === "yes_no";
  const itensDaSecao = secao?.items ?? [];
  const itemSelecionado = itensDaSecao.find((i) => i.id === selecionadoId) ?? null;
  const pctDe = (n: number) => (resumo.total > 0 ? Math.round((n / resumo.total) * 100) : 0);
  const avarias = resumo.atencao + resumo.naoConformes;

  return (
    <div className={`space-y-4 ${portal || casca ? "max-w-xl mx-auto" : ""} ${somenteLeitura ? "" : portal || casca ? "pb-44" : "pb-32"}`}>
      <PageHeader
        title="Checklist de Inspeção"
        subtitle={`${KIND_LABEL[run.kind]}${run.movement && run.kind !== "delivery" && run.kind !== "return_delivery" ? ` (${MOVEMENT_LABEL[run.movement].toLowerCase()})` : ""} · inspeção do veículo ${run.vehiclePlate} · ${version.template?.name ?? ""} v${version.version}`}
        badge={
          <>
            <span className={RUN_STATUS[run.status].badge}>{RUN_STATUS[run.status].label}</span>
            {(pendentes.length > 0 || fotosPendentes.length > 0) && !somenteLeitura && (
              <span className="badge-amber">{semRede ? "Salvo no dispositivo" : "Sincronização pendente"}</span>
            )}
          </>
        }
        actions={casca ? undefined : <Link className="btn-secondary" to={voltar}>Voltar</Link>}
      />

      {fotosPendentes.length > 0 && (
        <div className="card p-3 text-sm bg-warning-50 text-warning-700">
          {enviandoFotos
            ? `Enviando ${fotosPendentes.length} foto(s)…`
            : `${fotosPendentes.length} foto(s) guardada(s) neste aparelho${fotosPendentes.some((p) => p.lastError) ? " (sem conexão)" : ""}. Enviamos assim que possível. Não feche antes de concluir.`}
        </div>
      )}

      {(pendentes.length > 0 || enviando) && (
        <div className={`card p-3 text-sm ${semRede ? "bg-warning-50 text-warning-700" : "text-gray-600"}`}>
          {semRede
            ? `Sem conexão: ${pendentes.length} resposta(s) guardada(s) neste aparelho. Enviamos assim que a rede voltar.`
            : "Salvando…"}
        </div>
      )}

      {/* Celular: veículo, posição no checklist e progresso geral */}
      <div className={`card p-3 space-y-3 ${ehMobile ? "" : "hidden"}`}>
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 grid place-items-center w-16 h-14 rounded-xl bg-surface-muted text-tm-700">
            <IconTruck className="w-9 h-9" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-lg font-semibold text-gray-900 leading-tight">
              {run.vehiclePlate}{run.trailerPlate ? <span className="text-gray-400"> + {run.trailerPlate}</span> : null}
            </div>
            <div className="text-xs text-gray-500 truncate">{contexto.data?.vehicleTypeName ?? version.template?.name ?? KIND_LABEL[run.kind]}</div>
          </div>
        </div>
        {fluxo === "executar" && atualMobile ? (
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Item <strong>{posMobile + 1}</strong> de {plano.length}</span>
              <span className="font-medium text-gray-900">{Math.round(((posMobile + 1) / Math.max(plano.length, 1)) * 100)}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-tm-500 transition-all" style={{ width: `${((posMobile + 1) / Math.max(plano.length, 1)) * 100}%` }} />
            </div>
            {secao && <div className="mt-1.5 text-xs text-gray-500 truncate">{secao.title}</div>}
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">{resumo.feitos} de {resumo.total} itens</span>
              <span className="font-medium text-gray-900">{resumo.progresso}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className={`h-full transition-all ${corProgresso}`} style={{ width: `${resumo.progresso}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Desktop: contexto da inspeção — quatro cartões */}
      <div className={ehMobile ? "hidden" : "grid grid-cols-2 xl:grid-cols-4 gap-3"}>
        <CartaoContexto icone={<IconUser className="w-6 h-6" />} rotulo="Motorista" valor={run.driverName ?? "—"} nota={run.inspectorName ? `Responsável: ${run.inspectorName}` : undefined} />
        <CartaoContexto
          icone={<IconTruck className="w-6 h-6" />}
          rotulo={contexto.data?.vehicleTypeName ?? "Veículo"}
          valor={run.vehiclePlate}
          mono
          nota={run.odometerKm != null ? `${fmtNumber(run.odometerKm)} km` : run.lastKnownOdometerKm != null ? `${fmtNumber(run.lastKnownOdometerKm)} km · último conhecido` : "sem leitura de KM"}
        />
        <CartaoContexto icone={<IconTruck className="w-6 h-6" />} rotulo="Carreta / implemento" valor={run.trailerPlate ?? "—"} mono />
        <CartaoContexto
          icone={<IconCheck className="w-6 h-6" />}
          rotulo={concluida ? "Inspeção concluída" : "Data da inspeção"}
          valor={new Date(concluida && run.finishedAt ? run.finishedAt : run.startedAt).toLocaleDateString("pt-BR")}
          nota={new Date(concluida && run.finishedAt ? run.finishedAt : run.startedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        />
      </div>

      {/* O par da viagem: retorno ↔ saída */}
      <ParDaViagem run={run} portal={portal} />

      {/* Resumo (no celular some: o item é o foco) */}
      <div className={ehMobile ? "hidden" : "grid grid-cols-2 xl:grid-cols-4 gap-3"}>
        <CartaoResumo tom="neutro" icone={<IconWrench className="w-6 h-6" />} valor={resumo.total} rotulo="itens no checklist" />
        <CartaoResumo tom="ok" icone={<IconCheck className="w-6 h-6" />} valor={resumo.conformes} rotulo="conformes" pct={pctDe(resumo.conformes)} />
        <CartaoResumo tom="warn" icone={<IconAlertTriangle className="w-6 h-6" />} valor={avarias} rotulo={avarias === 1 ? "avaria" : "avarias"} pct={pctDe(avarias)} />
        <div className="rounded-xl border border-info-100 bg-info-50 px-4 py-3">
          <div className="flex items-center justify-between text-sm font-medium text-gray-900">
            <span>{concluida ? "Inspeção concluída" : run.status === "cancelled" ? "Inspeção cancelada" : "Inspeção em andamento"}</span>
            <span>{resumo.progresso}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
            <div className={`h-full transition-all ${corProgresso}`} style={{ width: `${resumo.progresso}%` }} />
          </div>
          <div className="mt-1.5 text-xs text-gray-500">{resumo.feitos} de {resumo.total} itens{resumo.pendentes > 0 ? ` · ${resumo.pendentes} pendente(s)` : ""}</div>
        </div>
      </div>

      {/* Inspeção concluída: o resultado e os caminhos seguintes */}
      {concluida && (
        <div className="card p-4 space-y-3">
          <div>
            <h3 className="font-semibold text-gray-900">Inspeção concluída</h3>
            <p className="text-sm text-gray-500">
              {resumo.total} itens verificados · {resumo.conformes} conformes · {resumo.atencao + resumo.naoConformes} avaria(s)
              {resumo.na > 0 ? ` · ${resumo.na} N/A` : ""}
            </p>
          </div>
          <p className={`text-sm ${(run.nonConformities?.length ?? 0) > 0 ? "text-danger-600 font-medium" : "text-tm-700"}`}>
            {(run.nonConformities?.length ?? 0) > 0
              ? `${run.nonConformities!.length} não conformidade(s) gerada(s)`
              : "Nenhuma não conformidade gerada."}
          </p>
          <div className="flex flex-wrap gap-2">
            {(run.nonConformities?.length ?? 0) > 0 && <Link className="btn-primary" to={ncsLink}>Ver não conformidades</Link>}
            <Link className="btn-secondary" to={voltar}>Voltar</Link>
            {portal && <Link className="btn-ghost" to={portalPaths.vehicle(run.vehiclePlate)}>Histórico do veículo</Link>}
          </div>
        </div>
      )}

      {fluxo !== "revisar" && (
        <>
          {/* Navegação por seções (desktop e tablet; no celular o item guia) */}
          {fluxo === "executar" && (
            <div className={`${ehMobile ? "hidden" : "flex"} overflow-x-auto rounded-xl border border-surface-border bg-white p-1 gap-1`}>
              {resumo.secoes.map((s, i) => {
                const ativa = i === secaoAtual;
                return (
                  <button
                    key={s.id}
                    onClick={() => irParaSecao(i)}
                    aria-current={ativa ? "step" : undefined}
                    className={`flex-1 min-w-[8.75rem] flex-shrink-0 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      ativa
                        ? "bg-tm-800 text-white"
                        : s.estado === "com_problema"
                          ? "bg-warning-50 text-warning-700 hover:bg-warning-100"
                          : "text-gray-600 hover:bg-surface-muted"
                    }`}
                  >
                    {s.estado === "completa" && <IconCheck className="w-4 h-4" />}
                    {s.estado === "com_problema" && <IconAlertTriangle className="w-4 h-4" />}
                    <span className="truncate">{s.title}</span>
                    <span className={`text-xs ${ativa ? "text-white/70" : "text-gray-400"}`}>{s.feitos}/{s.total}</span>
                  </button>
                );
              })}
            </div>
          )}

          {erro && <div className="card p-3 border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}

          {fluxo === "leitura" ? (
            // Concluído/cancelado: leitura, com as seções que expandem e recolhem.
            <div className="space-y-3">
              {version.sections.map((s, i) => {
                const r = resumo.secoes[i]!;
                return (
                  <details key={s.id} className="card" open={r.estado === "com_problema" || version.sections.length === 1}>
                    <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-2 text-sm font-medium text-gray-900">
                      <span>{s.title}</span>
                      <SecaoBadge estado={r.estado} resumo={r} />
                    </summary>
                    <div className="space-y-2 p-2 sm:p-3 border-t border-gray-100">
                      {s.items.map((it) => renderItem(it, s))}
                    </div>
                  </details>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {secao?.helpText && <p className="px-1 text-xs text-gray-500">{secao.helpText}</p>}
              {ehMobile ? (
                atualMobile && renderItem(atualMobile.it, secao, true)
              ) : (
                <div className="grid xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
                  <div className="space-y-3 min-w-0">
                    {/* Itens de tabela, em blocos contíguos; os de cartão quebram a tabela */}
                    {agruparItens(itensDaSecao, cabeNaTabela).map((bloco, bi) =>
                      bloco.tabela ? (
                        <div key={bi} className="card overflow-hidden" role="table">
                          <div className="grid grid-cols-[minmax(11.25rem,1.3fr)_auto_minmax(0,1fr)_20px] gap-3 px-3 py-2 text-xs font-medium text-gray-500 bg-surface-muted/60">
                            <span>Item</span>
                            <span className="min-w-[12.75rem]">Status</span>
                            <span>Observação</span>
                            <span />
                          </div>
                          {bloco.itens.map((item) => {
                            const p = propsDoItem(item, secao);
                            return (
                              <LinhaTabela
                                key={item.id}
                                item={item}
                                resposta={p.resposta}
                                estado={p.estado}
                                somenteLeitura={somenteLeitura}
                                selecionado={selecionadoId === item.id}
                                onSelecionar={() => setSelecionadoId(item.id)}
                                onResponder={p.onResponder}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        bloco.itens.map((item) => renderItem(item, secao))
                      ),
                    )}
                  </div>

                  <aside className="card p-4 xl:sticky xl:top-4" aria-label="Registro de avaria">
                    <div className="flex items-center gap-2 mb-3">
                      <IconWrench className="w-5 h-5 text-tm-700" />
                      <h3 className="font-semibold text-gray-900">Registro de avaria</h3>
                    </div>
                    {itemSelecionado && cabeNaTabela(itemSelecionado) ? (
                      <PainelAvaria {...propsDoItem(itemSelecionado, secao)} />
                    ) : (
                      <p className="text-sm text-gray-500">
                        Marque <strong>Avaria</strong> em um item para registrar o problema, a prioridade e a foto aqui.
                      </p>
                    )}
                  </aside>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Revisão: o que a conclusão vai fazer, antes de ela acontecer */}
      {fluxo === "revisar" && (
        <div className="card p-4 space-y-4">
          {erro && <div className="p-3 rounded-lg border border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}
          <div>
            <h3 className="font-semibold text-gray-900">Revisar inspeção</h3>
            <p className="text-sm text-gray-500">Confira o resultado antes de finalizar.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Resumo rotulo="Itens" valor={resumo.total} />
            <Resumo rotulo="Conformes" valor={resumo.conformes} tom="ok" />
            <Resumo rotulo="Avarias" valor={resumo.atencao + resumo.naoConformes} tom="warn" />
            <Resumo rotulo="Pendentes" valor={resumo.pendentes} />
          </div>
          <p className="text-sm text-gray-600">
            {resumo.observacoes} observação(ões) · {resumo.fotos} foto(s) · {resumo.ocorrencias} não conformidade(s) serão geradas
          </p>

          {!todosObrigatoriosOk && (
            <div className="rounded-lg bg-danger-50 border border-danger-600/30 p-3 text-sm space-y-1">
              <p className="font-medium text-danger-600">
                Existem {resumo.obrigatoriosPendentes.length} item(ns) obrigatório(s) pendente(s).
              </p>
              <ul className="space-y-1">
                {resumo.obrigatoriosPendentes.map((p) => (
                  <li key={p.itemId}>
                    <button className="text-left text-danger-600 underline underline-offset-2" onClick={() => irParaItem(p.itemId, p.secaoIndex)}>
                      {p.secaoTitle} → {p.itemLabel}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {semProblema.length > 0 && (
            <p className="text-sm text-warning-700">Escolha o problema (ou descreva o "Outro") em: {semProblema.map((i) => i.label).join(", ")}. Sem isso, a conclusão será recusada.</p>
          )}
          {semFotoObrigatoria.length > 0 && (
            <p className="text-sm text-warning-700">Falta foto obrigatória em: {semFotoObrigatoria.map((i) => i.label).join(", ")}. Sem isso, a conclusão será recusada.</p>
          )}

          <ul className="divide-y divide-gray-100 border border-surface-border rounded-lg">
            {resumo.secoes.map((s, i) => (
              <li key={s.id}>
                <button className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left hover:bg-surface-muted" onClick={() => irParaSecao(i)}>
                  <span className="text-gray-900">{s.title}</span>
                  <SecaoBadge estado={s.estado} resumo={s} />
                </button>
              </li>
            ))}
          </ul>

          {/* KM: a regra de regressão/justificativa é a de sempre (a API valida) */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex-1 min-w-[8.75rem]">
              <span className="text-xs text-gray-500">KM atual</span>
              <input
                className="input w-full mt-0.5"
                inputMode="numeric"
                placeholder={run.lastKnownOdometerKm != null ? String(run.lastKnownOdometerKm) : "KM do odômetro"}
                value={km}
                onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))}
              />
              {run.lastKnownOdometerKm != null && (
                <span className="text-xs text-gray-400">Último conhecido: {fmtNumber(run.lastKnownOdometerKm)} km{run.lastKnownOdometerSource ? ` (${run.lastKnownOdometerSource})` : ""}</span>
              )}
            </label>
            {km !== "" && run.lastKnownOdometerKm != null && Number(km) < run.lastKnownOdometerKm && (
              <label className="flex-1 min-w-[12.5rem]">
                <span className="text-xs text-warning-700">KM menor que o conhecido: motivo</span>
                <input
                  className="input w-full mt-0.5"
                  placeholder="Ex.: odômetro substituído em 12/09"
                  value={motivoKm}
                  onChange={(e) => setMotivoKm(e.target.value)}
                />
              </label>
            )}
          </div>
        </div>
      )}

      {/* Não conformidades já registradas (execução em andamento ou leitura) */}
      {!concluida && (run.nonConformities?.length ?? 0) > 0 && (
        <div className="card p-4">
          <h3 className="font-medium text-gray-900 mb-2">Não conformidades registradas</h3>
          <ul className="space-y-2 text-sm">
            {run.nonConformities!.map((nc) => (
              <li key={nc.id} className="flex items-center justify-between gap-2">
                <span>{nc.itemLabel}{nc.defectLabel ? `: ${nc.defectLabel}` : ""}</span>
                <CriticalityBadge value={nc.criticality} />
              </li>
            ))}
          </ul>
          <Link className="btn-secondary mt-3" to={ncsLink}>Abrir na tela de não conformidades</Link>
          {portal && (
            <Link className="btn-ghost mt-3 ml-2" to={portalPaths.vehicle(run.vehiclePlate)}>
              Histórico do veículo
            </Link>
          )}
        </div>
      )}
      {concluida && (run.nonConformities?.length ?? 0) > 0 && (
        <div className="card p-4">
          <h3 className="font-medium text-gray-900 mb-2">Não conformidades desta inspeção</h3>
          <ul className="space-y-2 text-sm">
            {run.nonConformities!.map((nc) => (
              <li key={nc.id} className="flex items-center justify-between gap-2">
                <Link className="text-tm-600 hover:underline" to={ncsLink}>{nc.itemLabel}{nc.defectLabel ? `: ${nc.defectLabel}` : ""}</Link>
                <CriticalityBadge value={nc.criticality} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cancelamento: fora da barra fixa, para não competir com "Finalizar" */}
      {!somenteLeitura && (
        <div className="card p-4">
          {!cancelando ? (
            <button className="text-sm text-danger-600 hover:underline" onClick={() => setCancelando(true)}>
              Cancelar este checklist
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                O checklist fica registrado como cancelado e não pode ser retomado.
              </p>
              <input
                className="input w-full"
                placeholder="Motivo (ex.: placa errada)"
                maxLength={500}
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
              />
              <div className="flex gap-2">
                <button className="btn-secondary" disabled={cancelar.isPending} onClick={() => setCancelando(false)}>
                  Voltar
                </button>
                <button className="btn-secondary border-danger-600/40 text-danger-600" disabled={cancelar.isPending} onClick={() => cancelar.mutate()}>
                  {cancelar.isPending ? "Cancelando…" : "Confirmar cancelamento"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {revisando && (
        <PhotoReview
          file={revisando.file}
          itemLabel={revisando.label}
          onCancel={() => setRevisando(null)}
          onUse={(blob) => void usarFoto(revisando.itemId, blob)}
        />
      )}

      {/* Confirmação: concluir fecha o checklist e abre as ocorrências — sem volta. */}
      {confirmando && !somenteLeitura && (
        <div className="fixed inset-0 z-40 bg-gray-900/40 flex items-end sm:items-center justify-center p-3" role="dialog" aria-modal="true" aria-label="Confirmar conclusão">
          <div className="card w-full max-w-md p-5 space-y-3 bg-white">
            <h3 className="text-base font-semibold text-gray-900">Finalizar inspeção?</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li><span className="font-mono">{run.vehiclePlate}</span>{run.trailerPlate ? <> + <span className="font-mono">{run.trailerPlate}</span></> : null} · KM {km ? fmtNumber(Number(km)) : "—"}</li>
              <li>{resumo.feitos} de {resumo.total} itens verificados</li>
              <li className={viramOcorrencia.length ? "text-danger-600 font-medium" : "text-tm-700"}>
                {viramOcorrencia.length
                  ? `${viramOcorrencia.length} item(ns) viram ocorrência: ${viramOcorrencia.slice(0, 4).map((i) => i.label).join(", ")}${viramOcorrencia.length > 4 ? "…" : ""}`
                  : "Nenhum item não conforme, nenhuma ocorrência será aberta."}
              </li>
            </ul>
            <p className="text-xs text-gray-500">Após a conclusão, este checklist não poderá ser alterado nem receber fotos novas.</p>
            <div className="flex gap-2 justify-end">
              <button className="btn-secondary" disabled={concluir.isPending} onClick={() => setConfirmando(false)}>Revisar</button>
              <button className="btn-primary" disabled={concluir.isPending} onClick={() => concluir.mutate()}>
                {concluir.isPending ? "Concluindo…" : "Confirmar conclusão"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra de ações, fixa — é o alvo do polegar (respeita a área segura) */}
      {!somenteLeitura && (
        <div className={`fixed left-0 right-0 bg-white border-t border-surface-border p-3 z-30 pb-[max(0.75rem,env(safe-area-inset-bottom))] ${
          portal ? "bottom-[calc(3.5rem+env(safe-area-inset-bottom))] md:bottom-0"
            : casca ? "bottom-[calc(3.625rem+env(safe-area-inset-bottom))] lg:bottom-0" : "bottom-0 lg:left-64"
        }`}>
          <div className={`${portal || casca ? "max-w-xl" : "max-w-5xl"} mx-auto flex items-center gap-2`}>
            {fluxo === "executar" ? (
              <>
                <button className={`btn-secondary ${ehMobile ? "hidden" : "inline-flex"}`} disabled={enviando} onClick={() => void salvarERetornar()}>
                  Salvar rascunho
                </button>
                <div className={`flex-1 flex gap-2 ${ehMobile ? "" : "justify-end"}`}>
                  <button
                    className={`btn-secondary ${ehMobile ? "flex-1 min-h-[3.25rem]" : ""}`}
                    disabled={ehMobile ? posMobile === 0 : secaoAtual === 0}
                    onClick={() => {
                      if (ehMobile) setIdxMobile(Math.max(posMobile - 1, 0));
                      else setSecaoAtual((i) => i - 1);
                      window.scrollTo({ top: 0 });
                    }}
                  >
                    Anterior
                  </button>
                  {ultimoDeTodos ? (
                    <button className={`btn-primary ${ehMobile ? "flex-1 min-h-[3.25rem]" : ""}`} onClick={() => { setErro(null); setEtapa("revisar"); window.scrollTo({ top: 0 }); }}>
                      Revisar inspeção
                    </button>
                  ) : (
                    <button
                      className={`btn-primary ${ehMobile ? "flex-1 min-h-[3.25rem]" : ""}`}
                      onClick={() => {
                        if (ehMobile) setIdxMobile(posMobile + 1);
                        else setSecaoAtual((i) => i + 1);
                        window.scrollTo({ top: 0 });
                      }}
                    >
                      {ehMobile ? "Próximo item →" : "Próxima seção"}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <button className={`btn-secondary ${ehMobile ? "flex-1 min-h-[3.25rem]" : ""}`} onClick={() => setEtapa("executar")}>Voltar à inspeção</button>
                <button
                  className={`btn-primary ${ehMobile ? "flex-1 min-h-[3.25rem]" : "ml-auto"}`}
                  disabled={!podeFinalizar}
                  onClick={() => { setErro(null); setConfirmando(true); }}
                >
                  Finalizar inspeção
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Resumo({ rotulo, valor, tom }: { rotulo: string; valor: number; tom?: "ok" | "warn" | "bad" }) {
  const cor = tom === "ok" ? "text-tm-700" : tom === "warn" ? "text-warning-700" : tom === "bad" ? "text-danger-600" : "text-gray-900";
  return (
    <div className="card px-3 py-2">
      <div className={`text-xl font-semibold leading-tight ${valor === 0 && tom ? "text-gray-400" : cor}`}>{valor}</div>
      <div className="text-xs text-gray-500">{rotulo}</div>
    </div>
  );
}

function SecaoBadge({ estado, resumo }: { estado: EstadoSecao; resumo: { feitos: number; total: number; atencao: number; naoConformes: number } }) {
  if (estado === "completa") return <span className="badge-green">✓ Completo</span>;
  if (estado === "com_problema") {
    return resumo.naoConformes > 0
      ? <span className="badge-red">✕ {resumo.naoConformes + resumo.atencao} avaria(s)</span>
      : <span className="badge-amber">⚠ {resumo.atencao} avaria(s)</span>;
  }
  return <span className="badge-gray">{resumo.feitos}/{resumo.total}</span>;
}

/** Cartão de contexto do topo (motorista, veículo, carreta, data). */
function CartaoContexto({ icone, rotulo, valor, nota, mono }: { icone: React.ReactNode; rotulo: string; valor: string; nota?: string; mono?: boolean }) {
  return (
    <div className="card p-3 flex items-center gap-3 min-w-0">
      <span className="flex-shrink-0 grid place-items-center w-11 h-11 rounded-xl bg-surface-muted text-tm-700">{icone}</span>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{rotulo}</div>
        <div className={`text-base font-semibold text-gray-900 leading-tight truncate ${mono ? "font-mono" : ""}`}>{valor}</div>
        {nota && <div className="text-xs text-gray-400 truncate">{nota}</div>}
      </div>
    </div>
  );
}

function CartaoResumo({ icone, valor, rotulo, pct, tom }: { icone: React.ReactNode; valor: number; rotulo: string; pct?: number; tom: "neutro" | "ok" | "warn" }) {
  const caixa = tom === "ok" ? "bg-tm-50 border-tm-100" : tom === "warn" ? "bg-warning-50 border-warning-100" : "bg-info-50 border-info-100";
  const bolinha = tom === "ok" ? "bg-tm-500 text-white" : tom === "warn" ? "bg-warning-500 text-white" : "bg-white text-info-600";
  return (
    <div className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${caixa}`}>
      <span className={`flex-shrink-0 grid place-items-center w-11 h-11 rounded-full ${bolinha}`}>{icone}</span>
      <div className="min-w-0 flex-1">
        <div className="text-2xl font-semibold text-gray-900 leading-none">{valor}</div>
        <div className="text-xs text-gray-600 mt-1">{rotulo}</div>
      </div>
      {pct != null && <span className="text-xs font-medium text-gray-500 self-start">{pct}%</span>}
    </div>
  );
}

/** Blocos contíguos de itens "de tabela" e "de cartão", na ordem do formulário. */
function agruparItens(itens: MaintenanceItemDto[], cabeNaTabela: (i: MaintenanceItemDto) => boolean) {
  const blocos: Array<{ tabela: boolean; itens: MaintenanceItemDto[] }> = [];
  for (const it of itens) {
    const tabela = cabeNaTabela(it);
    const ultimo = blocos[blocos.length - 1];
    if (ultimo && ultimo.tabela === tabela) ultimo.itens.push(it);
    else blocos.push({ tabela, itens: [it] });
  }
  return blocos;
}

/** Painel lateral: o registro do item selecionado na tabela. */
function PainelAvaria(p: ItemProps) {
  const { item, resposta, anterior, estado } = p;
  const { naoConforme } = avaliarItem(item, resposta, anterior);
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className="flex-shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-surface-muted text-tm-700">
          <ItemIcon code={item.code} label={item.label} className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <div className="text-xs text-gray-500">Item</div>
          <div className="text-sm font-semibold text-gray-900">{item.label}</div>
        </div>
        <div className="ml-auto">{estado !== "pendente" && ESTADO_CHIP[estado]}</div>
      </div>
      {naoConforme ? (
        <DetalheAvaria
          item={item}
          resposta={resposta}
          naoConforme
          anexos={p.anexos}
          fotosNaFila={p.fotosNaFila}
          somenteLeitura={p.somenteLeitura}
          pneus={p.pneus}
          onResponder={p.onResponder}
          onAnexo={p.onAnexo}
          onDescartarFila={p.onDescartarFila}
          onRemoverAnexo={p.onRemoverAnexo}
        />
      ) : (
        <p className="text-sm text-gray-500">
          Este item não tem avaria. Marque <strong>Avaria</strong> na linha para descrever o problema e anexar foto.
        </p>
      )}
    </div>
  );
}

function Campo({ rotulo, valor, nota, mono }: { rotulo: string; valor: string; nota?: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{rotulo}</div>
      <div className={`text-gray-900 font-medium ${mono ? "font-mono" : ""}`}>{valor}</div>
      {nota && <div className="text-xs text-gray-400">{nota}</div>}
    </div>
  );
}

interface ItemProps {
  item: MaintenanceItemDto;
  resposta?: MaintenanceAnswerDto;
  /** o que esta placa contou no checklist anterior */
  anterior?: MaintenancePreviousCountDto;
  anexos: MaintenanceAttachmentDto[];
  somenteLeitura: boolean;
  salvando: boolean;
  estado: EstadoItem;
  onResponder: (valores: Omit<PendingAnswer, "itemId" | "queuedAt">) => void;
  onAnexo: (file: File) => void;
  /** fotos conferidas deste item ainda no aparelho */
  fotosNaFila: PendingPhoto[];
  onDescartarFila: (fotoId: string) => void;
  onRemoverAnexo: (anexoId: string) => void;
  /** item de pneu: opções de pneu/posição do mapa do veículo */
  pneus?: RunTireOption[];
}

const ESTADO_CHIP: Record<Exclude<EstadoItem, "pendente">, React.ReactNode> = {
  conforme: <span className="badge-green">Conforme</span>,
  na: <span className="badge-gray">N/A</span>,
  atencao: <span className="badge-amber">Avaria</span>,
  nao_conforme: <span className="badge-red">Avaria</span>,
};

type Escolha = { value: string; label: string; tone: "ok" | "bad" | "neutral" };

/**
 * Valores que o PUT regrava junto com qualquer mudança — o PUT grava o item
 * inteiro, e mandar só um campo apagaria o mapa, a contagem ou o pneu.
 */
function valoresAtuais(resposta: MaintenanceAnswerDto | undefined, mapa: MaintenanceDamageMapDto | null) {
  return {
    optionValue: resposta?.optionValue ?? null,
    numberValue: resposta?.numberValue != null ? Number(resposta.numberValue) : null,
    jsonValue: mapa,
    observation: resposta?.observation ?? null,
    criticality: resposta?.criticality ?? null,
    defectCode: resposta?.defectCode ?? null,
    ...(resposta?.tireId !== undefined ? { tireId: resposta.tireId } : {}),
    ...(resposta?.tirePositionId !== undefined ? { tirePositionId: resposta.tirePositionId } : {}),
  };
}

function escolhasDoItem(item: MaintenanceItemDto): Escolha[] | null {
  if (item.answerType === "conformity") return CONFORMITY_CHOICES;
  if (item.answerType === "yes_no") return YES_NO_CHOICES;
  if (item.answerType === "choice") {
    return (item.options ?? []).map((o) => ({ value: o.value, label: o.label, tone: o.nonConforming ? ("bad" as const) : ("ok" as const) }));
  }
  return null;
}

function IconeEscolha({ tone }: { tone: Escolha["tone"] }) {
  if (tone === "ok") return <IconCheck className="w-4 h-4" />;
  if (tone === "bad") return <IconAlertTriangle className="w-4 h-4" />;
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" /><path d="M8 12h8" />
    </svg>
  );
}

/** Botão de resposta: o mesmo componente na tabela (compacto) e no celular (grande). */
function BotaoResposta({ escolha, ativo, disabled, tamanho, onClick }: {
  escolha: Escolha; ativo: boolean; disabled: boolean; tamanho: "tabela" | "normal" | "grande"; onClick: () => void;
}) {
  const tom = {
    ok: { on: "bg-tm-500 text-white border-tm-500", off: "bg-tm-50 text-tm-700 border-tm-100" },
    bad: { on: "bg-warning-500 text-white border-warning-500", off: "bg-warning-50 text-warning-700 border-warning-500/30" },
    neutral: { on: "bg-gray-500 text-white border-gray-500", off: "bg-gray-100 text-gray-600 border-gray-200" },
  }[escolha.tone];
  const medida =
    tamanho === "grande" ? "w-full min-h-[3.25rem] text-base gap-2"
      : tamanho === "tabela" ? "min-h-[2rem] min-w-[4rem] px-2.5 text-xs gap-1"
        : "flex-1 min-h-[2.75rem] px-3 text-sm gap-1.5";
  return (
    <button
      disabled={disabled}
      aria-pressed={ativo}
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-lg border font-medium transition-colors disabled:opacity-60 ${medida} ${ativo ? tom.on : tom.off}`}
    >
      {(ativo || tamanho === "grande") && <IconeEscolha tone={escolha.tone} />}
      {escolha.label}
    </button>
  );
}

const BORDA_ESTADO: Record<EstadoItem, string> = {
  nao_conforme: "border-danger-600/40",
  atencao: "border-warning-500/50",
  conforme: "border-surface-border",
  na: "border-surface-border",
  pendente: "border-surface-border",
};

function ItemLinha({ item, resposta, anterior, anexos, somenteLeitura, estado, onResponder, onAnexo, fotosNaFila, onDescartarFila, onRemoverAnexo, pneus, celular }: ItemProps & { celular?: boolean }) {
  const quantidade = resposta?.numberValue != null ? Number(resposta.numberValue) : null;
  const { naoConforme, abaixoDoMinimo, faltando, mapa } = avaliarItem(item, resposta, anterior);
  const observacao = resposta?.observation ?? "";
  const atual = () => valoresAtuais(resposta, mapa);
  const escolhas = escolhasDoItem(item);
  const empilhar = celular && !!escolhas && escolhas.length <= 3;

  return (
    <div className={`rounded-lg border p-4 bg-white ${BORDA_ESTADO[estado]}`}>
      <div className="flex items-start gap-3 mb-3">
        <span className={`flex-shrink-0 grid place-items-center rounded-xl bg-surface-muted text-tm-700 ${celular ? "w-14 h-14" : "w-10 h-10"}`}>
          <ItemIcon code={item.code} label={item.label} className={celular ? "w-8 h-8" : "w-5 h-5"} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-semibold text-gray-900 ${celular ? "text-lg leading-snug" : "text-sm"}`}>
            {item.label}
            {item.required && <span className="text-danger-600 ml-1">*</span>}
          </p>
          {item.helpText && <p className="text-xs text-gray-500 mt-0.5">{item.helpText}</p>}
          <div className="mt-1 flex items-center gap-1 flex-wrap">
            {estado !== "pendente" && ESTADO_CHIP[estado]}
            {observacao && <span className="badge-gray" title="Tem observação">Obs.</span>}
            {anexos.length + fotosNaFila.length > 0 && (
              <span className="badge-gray" title="Tem foto"><IconCamera className="w-3 h-3 inline -mt-0.5" /> {anexos.length + fotosNaFila.length}</span>
            )}
            {item.minQuantity != null && <span className="badge-gray">mín. {item.minQuantity}</span>}
            {item.criticality === "high" && <CriticalityBadge value={item.criticality} />}
          </div>
        </div>
      </div>

      {escolhas && (
        <div className={empilhar ? "flex flex-col gap-2" : `flex gap-2 ${escolhas.length > 3 ? "flex-wrap" : ""}`}>
          {escolhas.map((e) => (
            <BotaoResposta
              key={e.value}
              escolha={e}
              ativo={resposta?.optionValue === e.value}
              disabled={somenteLeitura}
              tamanho={empilhar ? "grande" : "normal"}
              onClick={() => onResponder({ ...atual(), optionValue: e.value })}
            />
          ))}
        </div>
      )}

      {(item.answerType === "quantity" || item.answerType === "number") && (
        <input
          className="input w-32"
          inputMode="numeric"
          disabled={somenteLeitura}
          defaultValue={resposta?.numberValue != null ? String(resposta.numberValue) : ""}
          placeholder="0"
          onBlur={(e) => {
            const v = e.target.value.trim();
            onResponder({ numberValue: v === "" ? null : Number(v), observation: observacao || null, criticality: resposta?.criticality ?? null });
          }}
        />
      )}

      {item.answerType === "damage_map" && (
        <DamageMap
          value={mapa}
          readOnly={somenteLeitura}
          onChange={(v) => onResponder({ ...atual(), jsonValue: v })}
        />
      )}

      {(item.answerType === "text" || item.answerType === "photo") && (
        <textarea
          className="input w-full"
          rows={2}
          disabled={somenteLeitura}
          defaultValue={resposta?.textValue ?? ""}
          placeholder={item.answerType === "photo" ? "Legenda da foto" : "Digite aqui"}
          onBlur={(e) => onResponder({ textValue: e.target.value.trim() || null, observation: observacao || null })}
        />
      )}

      {anterior && (
        <p className="mt-1 text-xs text-gray-500">
          Último checklist: <strong>{anterior.value}</strong> em {new Date(anterior.countedAt).toLocaleDateString("pt-BR")}
        </p>
      )}
      {faltando != null && (
        <p className="mt-1 text-xs text-danger-600">
          Faltam {faltando} em relação ao último checklist. Vira ocorrência ao concluir.
        </p>
      )}
      {abaixoDoMinimo && (
        <p className="mt-1 text-xs text-danger-600">Abaixo do mínimo de {item.minQuantity}. Vira ocorrência ao concluir.</p>
      )}

      <DetalheAvaria
        item={item}
        resposta={resposta}
        naoConforme={naoConforme}
        anexos={anexos}
        fotosNaFila={fotosNaFila}
        somenteLeitura={somenteLeitura}
        pneus={pneus}
        sempreObs={celular && item.allowsObservation}
        onResponder={onResponder}
        onAnexo={onAnexo}
        onDescartarFila={onDescartarFila}
        onRemoverAnexo={onRemoverAnexo}
      />
    </div>
  );
}

interface DetalheProps {
  item: MaintenanceItemDto;
  resposta?: MaintenanceAnswerDto;
  naoConforme: boolean;
  anexos: MaintenanceAttachmentDto[];
  fotosNaFila: PendingPhoto[];
  somenteLeitura: boolean;
  pneus?: RunTireOption[];
  /** mostra o campo de observação mesmo sem avaria (celular: "Observação (opcional)") */
  sempreObs?: boolean;
  onResponder: ItemProps["onResponder"];
  onAnexo: (file: File) => void;
  onDescartarFila: (fotoId: string) => void;
  onRemoverAnexo: (anexoId: string) => void;
}

/**
 * O que se registra quando o item tem avaria: qual o problema, qual pneu,
 * gravidade, descrição e foto. Serve ao cartão do item (celular) e ao painel
 * lateral "Registro de avaria" (desktop) — uma regra, dois lugares.
 */
function DetalheAvaria({ item, resposta, naoConforme, anexos, fotosNaFila, somenteLeitura, pneus, sempreObs, onResponder, onAnexo, onDescartarFila, onRemoverAnexo }: DetalheProps) {
  const inputFoto = useRef<HTMLInputElement>(null);
  const [obs, setObs] = useState(resposta?.observation ?? "");
  useEffect(() => { setObs(resposta?.observation ?? ""); }, [resposta?.observation]);

  const mapa = (resposta?.jsonValue ?? null) as MaintenanceDamageMapDto | null;
  const atual = () => ({ ...valoresAtuais(resposta, mapa), observation: obs || null });
  // Criticidade efetiva: a do item é o piso; o campo só pode elevar.
  const criticidade = resposta?.criticality ?? item.criticality;
  const pisoIdx = ORDEM_CRITICIDADE.indexOf(item.criticality);
  // Mesma regra da API: o item decide; Alta só obriga foto quando o campo ELEVOU.
  const exigeFoto = item.requiresPhotoOnNonConformity || (criticidade === "high" && item.criticality !== "high");
  const problemas = item.defectOptions ?? [];
  const problema = problemas.find((o) => o.value === resposta?.defectCode) ?? null;
  const totalFotos = anexos.length + fotosNaFila.length;

  return (
    <>
      {naoConforme && !somenteLeitura && (
        <p className="mt-3 text-xs font-medium text-danger-600">Problema identificado. Este item irá gerar uma Não Conformidade.</p>
      )}

      {/* Avaria: QUAL o problema, entre os cadastrados para o item. Obrigatório. */}
      {naoConforme && problemas.length > 0 && (
        <div className="mt-2">
          <p className={`text-xs mb-1 ${problema ? "text-gray-500" : "text-danger-600 font-medium"}`}>
            Qual é o problema?{problema ? "" : " (obrigatório)"}
          </p>
          <div className="flex flex-wrap gap-2">
            {problemas.map((o) => {
              const ativo = problema?.value === o.value;
              return (
                <button
                  key={o.value}
                  disabled={somenteLeitura}
                  onClick={() => onResponder({ ...atual(), defectCode: o.value })}
                  aria-pressed={ativo}
                  className={`min-h-[2.5rem] px-3 rounded-lg border text-sm transition-colors disabled:opacity-60 ${
                    ativo ? "bg-danger-600 text-white border-danger-600" : "bg-white text-gray-700 border-surface-border"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Item de pneu com problema: QUAL pneu (opcional — sem mapa, segue na observação). */}
      {naoConforme && pneus && (
        <label className="mt-2 block">
          <span className="text-xs text-gray-500">Qual pneu? (opcional)</span>
          <select
            className="input w-full mt-0.5"
            disabled={somenteLeitura}
            value={resposta?.tirePositionId ?? ""}
            onChange={(e) => {
              const op = pneus.find((o) => o.positionId === e.target.value);
              onResponder({ ...atual(), tireId: op?.tireId ?? null, tirePositionId: op?.positionId ?? null });
            }}
          >
            <option value="">Não identificado</option>
            {pneus.map((o) => <option key={o.key} value={o.positionId}>{o.label}</option>)}
          </select>
        </label>
      )}

      {/* Gravidade logo abaixo da resposta. O item define o piso. */}
      {naoConforme && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1">Prioridade</p>
          <div className="flex gap-2">
            {ORDEM_CRITICIDADE.map((c, idx) => {
              const ativo = criticidade === c;
              const abaixoDoPiso = idx < pisoIdx;
              return (
                <button
                  key={c}
                  disabled={somenteLeitura || abaixoDoPiso}
                  onClick={() => onResponder({ ...atual(), criticality: c })}
                  className={`flex-1 min-h-[2.5rem] rounded-lg border text-sm transition-colors disabled:opacity-40 ${
                    ativo
                      ? c === "high" ? "bg-danger-600 text-white border-danger-600" : "bg-warning-500 text-white border-warning-500"
                      : "bg-white text-gray-600 border-surface-border"
                  }`}
                >
                  {CRITICALITY[c].label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Observação e foto: no OK de mesa não poluem; no celular a observação fica à mão. */}
      {item.allowsObservation && (naoConforme || obs || sempreObs) && (
        <label className="mt-3 block">
          <span className="text-xs text-gray-500">{naoConforme ? "Descrição" : "Observação (opcional)"}</span>
          <textarea
            className="input w-full mt-0.5"
            rows={2}
            disabled={somenteLeitura}
            value={obs}
            placeholder={problema?.requiresObservation ? "Descreva o problema (obrigatório para \"Outro\")" : naoConforme ? "Detalhe, se precisar (lado, posição…)" : "Digite uma observação…"}
            onChange={(e) => setObs(e.target.value)}
            onBlur={() => { if (obs !== (resposta?.observation ?? "")) onResponder(atual()); }}
          />
        </label>
      )}

      {!somenteLeitura && (naoConforme || item.answerType === "photo") && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <input
            ref={inputFoto}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onAnexo(f);
              e.target.value = "";
            }}
          />
          <button className="btn-secondary min-h-[2.75rem]" onClick={() => inputFoto.current?.click()}>
            <IconCamera className="w-4 h-4" />
            {totalFotos > 0 ? "Outra foto" : "Adicionar foto"}
          </button>
          {exigeFoto && naoConforme && totalFotos === 0 && (
            <span className="text-xs text-danger-600">Foto obrigatória para concluir.</span>
          )}
        </div>
      )}

      {totalFotos > 0 && (
        <div className="mt-2 flex gap-2 flex-wrap">
          {anexos.map((a) => (
            <Removivel key={a.id} ativo={!somenteLeitura} rotulo="Remover foto" onRemover={() => onRemoverAnexo(a.id)}>
              <EvidenceThumb anexo={a} />
            </Removivel>
          ))}
          {fotosNaFila.map((p) => (
            <Removivel key={p.id} ativo={!somenteLeitura} rotulo="Descartar foto" onRemover={() => onDescartarFila(p.id)}>
              <FotoNaFila foto={p} />
            </Removivel>
          ))}
        </div>
      )}
    </>
  );
}

/** Linha da tabela (desktop): resposta de um clique, observação resumida, abre o painel. */
function LinhaTabela({ item, resposta, estado, somenteLeitura, selecionado, onSelecionar, onResponder }: {
  item: MaintenanceItemDto;
  resposta?: MaintenanceAnswerDto;
  estado: EstadoItem;
  somenteLeitura: boolean;
  selecionado: boolean;
  onSelecionar: () => void;
  onResponder: ItemProps["onResponder"];
}) {
  const escolhas = escolhasDoItem(item) ?? [];
  const mapa = (resposta?.jsonValue ?? null) as MaintenanceDamageMapDto | null;
  const observacao = resposta?.observation ?? "";
  return (
    <div
      role="row"
      onClick={onSelecionar}
      className={`grid grid-cols-[minmax(11.25rem,1.3fr)_auto_minmax(0,1fr)_20px] items-center gap-3 px-3 py-2 border-t border-gray-100 cursor-pointer transition-colors ${
        selecionado ? "bg-tm-50/70 ring-1 ring-inset ring-tm-200" : estado === "nao_conforme" || estado === "atencao" ? "bg-warning-50/40" : "hover:bg-surface-muted"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex-shrink-0 grid place-items-center w-8 h-8 rounded-lg bg-surface-muted text-tm-700">
          <ItemIcon code={item.code} label={item.label} className="w-4 h-4" />
        </span>
        <span className="text-sm text-gray-900 truncate" title={item.label}>
          {item.label}
          {item.required && <span className="text-danger-600 ml-0.5">*</span>}
        </span>
      </div>
      <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
        {escolhas.map((e) => (
          <BotaoResposta
            key={e.value}
            escolha={e}
            ativo={resposta?.optionValue === e.value}
            disabled={somenteLeitura}
            tamanho="tabela"
            onClick={() => {
              onSelecionar();
              onResponder({ ...valoresAtuais(resposta, mapa), optionValue: e.value });
            }}
          />
        ))}
      </div>
      <span className="text-xs text-gray-500 truncate" title={observacao || undefined}>{observacao || "—"}</span>
      <IconChevronRight className="w-4 h-4 text-gray-400" />
    </div>
  );
}

/** Foto conferida que ainda está no aparelho — com o estado do envio. */
function FotoNaFila({ foto }: { foto: PendingPhoto }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(foto.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [foto.blob]);
  return (
    <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-warning-400/60 bg-surface-muted" title={foto.lastError ?? "Aguardando envio"}>
      {url && <img src={url} alt="Foto aguardando envio" className="w-full h-full object-cover opacity-70" />}
      <span className="absolute bottom-0 inset-x-0 bg-warning-500/90 text-white text-[0.625rem] text-center py-0.5">
        {foto.lastError ? "sem conexão" : "enviando…"}
      </span>
    </div>
  );
}

/**
 * Miniatura com remoção em DOIS toques: no pátio, com luva, o X é fácil de
 * acertar sem querer — e a foto removida era a evidência.
 */
function Removivel({ ativo, rotulo, onRemover, children }: {
  ativo: boolean; rotulo: string; onRemover: () => void; children: React.ReactNode;
}) {
  const [confirmando, setConfirmando] = useState(false);
  if (!ativo) return <>{children}</>;
  return (
    <div className="relative">
      {children}
      {confirmando ? (
        <div className="absolute inset-0 rounded-lg bg-gray-900/70 flex flex-col items-center justify-center gap-1 p-1">
          <button className="text-[0.6875rem] font-medium text-white bg-danger-600 rounded px-1.5 py-0.5" onClick={() => { setConfirmando(false); onRemover(); }}>
            Remover
          </button>
          <button className="text-[0.6875rem] text-white/80" onClick={() => setConfirmando(false)}>Manter</button>
        </div>
      ) : (
        <button
          aria-label={rotulo}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-surface-border text-gray-500 text-xs shadow-sm"
          onClick={() => setConfirmando(true)}
        >
          ×
        </button>
      )}
    </div>
  );
}

/**
 * Retorno mostra a saída que fecha (e o KM rodado entre as duas); saída mostra o
 * retorno que a fechou. Sem par, diz por quê — o gestor não precisa adivinhar.
 */
function ParDaViagem({ run, portal }: { run: MaintenanceRunDetailDto["run"]; portal: boolean }) {
  if (!run.movement) return null;
  const link = (id: string) => (portal ? portalPaths.run(id) : `/frota/manutencao/checklists/${id}`);
  const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

  if (run.movement === "arrival") {
    const saida = run.relatedRun;
    const rodado = saida?.odometerKm != null && run.odometerKm != null ? run.odometerKm - saida.odometerKm : null;
    return (
      <div className="card p-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="badge-gray">Retorno</span>
        {saida ? (
          <>
            <span className="text-gray-600">
              Saída em <Link className="text-tm-600 hover:underline" to={link(saida.id)}>{quando(saida.finishedAt ?? saida.startedAt)}</Link>
              {saida.odometerKm != null ? ` · ${fmtNumber(saida.odometerKm)} km` : ""}
              {saida.driverName ? ` · ${saida.driverName}` : ""}
            </span>
            {rodado != null && rodado >= 0 && <span className="text-gray-900 font-medium">{fmtNumber(rodado)} km desde a saída</span>}
          </>
        ) : (
          <span className="text-gray-500">Sem saída registrada no SIAG para esta placa (saída no papel ou já fechada).</span>
        )}
      </div>
    );
  }

  const retorno = (run.pairedRuns ?? []).find((p) => p.movement === "arrival" && p.status !== "cancelled");
  return (
    <div className="card p-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="badge-gray">Saída</span>
      {retorno ? (
        <span className="text-gray-600">
          Retorno em <Link className="text-tm-600 hover:underline" to={link(retorno.id)}>{quando(retorno.finishedAt ?? retorno.startedAt)}</Link>
          {retorno.status !== "completed" ? " (em andamento)" : ""}
        </span>
      ) : (
        <span className="text-gray-500">Veículo ainda não retornou.</span>
      )}
    </div>
  );
}

/** Abaixo de `md` (768px) a execução vira um item por vez. */
function useEhMobile(sempre = false): boolean {
  const consulta = "(max-width: 767px)";
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(consulta).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(consulta);
    const f = () => setMobile(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return sempre || mobile;
}
